/**
 * Closed captions for the race audio (plan P2 "Captions"): which radio clip and which commentary
 * line are on screen. A pure function of the clock (like every other frame input), so seek == play.
 *
 * - Radio: shown for RADIO_CAPTION_S presentation seconds from its trigger time, in every mode.
 *   The trigger is `t_speech` for a second-granular race, or the moment the car starts the clip's lap
 *   for a lap-granular one (the label then says "AROUND LAP N"). One caption at most: the newest
 *   trigger wins.
 * - Commentary: the line's `dur` from its `t_pres` (Director presentation time; in free mode the
 *   line's race time is mapped through the warp and the age counted in presentation seconds).
 */
import type { CaptionApi, CaptionState, CommentaryFile, LapsFile, RadioFile, RGB } from '../core/api.ts';
import { takeCount } from '../core/audio/takes.ts';
import type { AudioState, RadioClip } from '../core/audio/types.ts';

export const RADIO_CAPTION_S = 6;

export interface CaptionInput {
  /** presentation seconds (Director video time) */
  presT: number;
  mode: 'director' | 'free';
  /** presentation seconds since a race time (`clock.presentationAge`) */
  age(tRace: number): number;
  /** engine state; its unavailable marks only count while sound is on */
  audio?: AudioState | undefined;
}

export interface CaptionSources {
  laps: Pick<LapsFile, 'laps' | 'leader_lap_starts'>;
  /** driver code -> team colour, or null for an unknown code */
  teamRgb(code: string): RGB | null;
  /** Director presentation time -> race time (free-mode commentary) */
  raceOfPres?: (t: number) => number;
}

export const ENGINE_LABEL = 'Engine sound synthesized from telemetry, not a recording';
export const ENGINE_LABEL_MODELLED = 'ENGINE: MODELLED FROM RECONSTRUCTED SPEED';
export const COMMENTARY_LABEL = 'Synthetic commentary: AI-written from race data, synthetic voice';
export const MUSIC_LABEL = 'Music: procedural';
export const CROWD_LABEL = 'Crowd: procedural';
export const RADIO_AUDIO_LABEL = 'Team radio © Formula 1';

/** host of a source URL, `www.` stripped; a malformed URL skips its entry (audit 2 fix 9: `new URL` throws, and one bad line must not brick START) */
function hostnamesOf(list: { url: string }[] | undefined): string[] {
  const out: string[] = [];
  for (const s of list ?? []) {
    try {
      out.push(new URL(s.url).hostname.replace(/^www\./, ''));
    } catch {
      // not a URL: the entry contributes no host
    }
  }
  return [...new Set(out)];
}

/** The honesty labels for what this race can actually play (plan §1): radio audio only when a clip has a `src_url`.
 *  `take` (0-based) names the commentary take in the list when the file ships more than one (C10). */
export function audioLabels(radio: RadioFile | null, commentary: CommentaryFile | null, take = 0, modelledEngine = false): string[] {
  const out = [modelledEngine ? ENGINE_LABEL_MODELLED : ENGINE_LABEL, MUSIC_LABEL, CROWD_LABEL];
  if (commentary && commentary.lines.length > 0) {
    out.push(commentary.label || COMMENTARY_LABEL);
    const k = takeCount(commentary);
    if (k > 1) out.push(`Commentary take ${String(take + 1)} of ${String(k)}`);
    const hosts = hostnamesOf(commentary.sources);
    if (hosts.length > 0) out.push(`Circuit notes: ${hosts.join(', ')} circuit guide`);
    const race = hostnamesOf(commentary.race_sources);
    if (race.length > 0) out.push(`Race facts: ${race.slice(0, 4).join(', ')}${race.length > 4 ? ` and ${String(race.length - 4)} more` : ''}`);
  }
  if (radio?.clips.some((c) => c.src_url)) out.push(RADIO_AUDIO_LABEL);
  return out;
}

const GREY: RGB = [150, 152, 160];

/** The 5x7 pixel font has no `"` or `;` and no typographic punctuation: map them to the glyphs it has. */
export function plain(s: string): string {
  return s.replace(/[\u2018\u2019\u201c\u201d"]/g, "'").replace(/[\u2013\u2014]/g, '-').replace(/\u2026/g, '...').replace(/;/g, ',').replace(/\s+/g, ' ').trim();
}

export class CaptionController implements CaptionApi {
  on = true;
  state: CaptionState = { radio: null, commentary: null };
  key = '';
  private radio: RadioFile | null = null;
  private commentary: CommentaryFile | null = null;
  private trig: number[] = [];
  private lapGranular = false;

  constructor(private readonly src: CaptionSources) {}

  /** the race's files, once they have loaded (they never block boot) */
  setFiles(radio: RadioFile | null, commentary: CommentaryFile | null): void {
    this.radio = radio;
    this.commentary = commentary;
    this.lapGranular = radio?.lag.granularity === 'lap';
    this.trig = (radio?.clips ?? []).map((c) => (this.lapGranular ? this.lapStart(c) : c.t_speech));
  }

  /** when the clip's car starts the clip's lap (falls back to the leader's lap start, then the clip time) */
  private lapStart(c: RadioClip): number {
    const row = this.src.laps.laps[c.driver]?.find((r) => r.lap === c.lap);
    return row?.start ?? this.src.laps.leader_lap_starts[c.lap - 1] ?? c.t_speech;
  }

  update(i: CaptionInput): void {
    const r = this.radioAt(i), c = this.commentaryAt(i);
    this.state = { radio: r, commentary: c };
    this.key = `${this.on ? '1' : '0'}|${r ? `${r.id}${r.audioUnavailable ? '!' : ''}` : ''}|${c ? `${c.id}${c.voiceUnavailable ? '!' : ''}` : ''}`;
  }

  private radioAt(i: CaptionInput): CaptionState['radio'] {
    const clips = this.radio?.clips ?? [];
    let best = -1;
    for (let k = 0; k < clips.length; k++) {
      const a = i.age(this.trig[k] ?? 0);
      if (a < 0 || a >= RADIO_CAPTION_S) continue;
      if (best < 0 || (this.trig[k] ?? 0) >= (this.trig[best] ?? 0)) best = k;
    }
    const c = clips[best];
    if (!c) return null;
    const lap = `${this.lapGranular ? 'AROUND LAP ' : 'L'}${String(c.lap)}`;
    return {
      id: c.id,
      driver: c.driver,
      rgb: this.src.teamRgb(c.driver) ?? GREY,
      text: plain(c.paraphrase),
      quote: c.quote ? plain(c.quote) : null,
      label: `TEAM RADIO · ${lap}`,
      audioUnavailable: !!i.audio?.enabled && i.audio.radio.unavailable.includes(c.id),
    };
  }

  private commentaryAt(i: CaptionInput): CaptionState['commentary'] {
    const lines = this.commentary?.lines ?? [];
    for (const l of lines) {
      const a = i.mode === 'director' ? i.presT - l.t_pres : this.src.raceOfPres ? i.age(this.src.raceOfPres(l.t_pres)) : -1;
      if (a < 0 || a >= l.dur) continue;
      const au = i.audio;
      return { id: l.id, text: plain(l.text), voiceUnavailable: !!au?.enabled && (au.commentary.voiceUnavailable || au.commentary.unavailable.includes(l.id)) };
    }
    return null;
  }
}
